import type { LayoutOptions, StudioChoice } from '@base/shared';
import { LayoutGrid, Menu } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FinishPreviewContext } from '../platform.tsx';
import { IsolatedFrame } from './IsolatedFrame.tsx';
import {
  Column,
  FocusBar,
  Inspector,
  ListColumn,
  Rail,
  Ribbon,
  SheetTabs,
  Sidebar,
  StatusBar,
  SubStrip,
  Tiles,
  TopBar,
  WorkTabs,
} from './PreviewChrome.tsx';
import { samplesFor, SampleIcon, type SampleDomain } from './samples/index.ts';
import { ScreenView, type PreviewScreen, type PreviewState } from './screens/index.tsx';
import { useScope } from './scope.tsx';
import { reviewOf, resolvedStudio } from './state.ts';

export const PREVIEW_DEVICES = ['desk', 'tablet', 'phone'] as const;
export type PreviewDevice = (typeof PREVIEW_DEVICES)[number];
export const PREVIEW_READINGS = ['normal', 'large', 'contrast'] as const;
export type PreviewReading = (typeof PREVIEW_READINGS)[number];

/** A largura de cada aparelho; a prévia é desenhada nela e reduzida para caber na caixa. */
const WIDTHS: Record<PreviewDevice, number> = { desk: 1280, tablet: 820, phone: 390 };

/** Em que largura desenhar a prévia e quanto reduzir para caber na caixa. */
export function previewFit(
  device: PreviewDevice,
  boxWidth: number,
): { width: number; zoom: number } {
  const base = WIDTHS[device];
  if (boxWidth <= 0) return { width: base, zoom: 0.6 };
  const width = device === 'desk' ? Math.max(base, boxWidth) : base;
  return { width, zoom: Math.min(1, boxWidth / width) };
}

/**
 * A prévia viva do estúdio (ADR 0133, parte 3): um sistema de verdade em miniatura, na moldura do
 * layout escolhido, com a tela de exemplo dentro, nas cores, na letra e no acabamento da escolha.
 * Desenhada na largura do aparelho e reduzida para caber; a altura acompanha a tela.
 */
export function StudioPreview({
  value,
  appName,
  screen = 'list',
  domain = 'erp',
  state = 'normal',
  reading = 'normal',
  device = 'desk',
  dark = false,
  noColor = false,
  maxHeight,
  brand,
}: {
  value: StudioChoice;
  appName: string;
  /** O nome e o logo do sistema; sem eles, a prévia usa a marca do exemplo. */
  brand?: { name: string; logo: string };
  /** Sobra da prévia antiga; a marca vem do exemplo. */
  symbolUrl?: string;
  screen?: PreviewScreen;
  domain?: string;
  state?: PreviewState;
  reading?: PreviewReading;
  device?: PreviewDevice;
  dark?: boolean;
  /** Na etapa Layout, sem cor, para julgar só a estrutura. */
  noColor?: boolean;
  /** Corta a prévia nesta altura (comparação lado a lado). */
  maxHeight?: number;
}) {
  const { t, i18n } = useTranslation();
  const sample =
    samplesFor(i18n.language).find((x) => x.id === domain) ?? samplesFor(i18n.language)[0]!;
  const d: SampleDomain = brand
    ? { ...sample, brand: [brand.name, sample.brand[1], initialsOf(brand.name)], logo: brand.logo }
    : sample;
  const { theme } = reviewOf(value, appName);
  const { layout, finish } = resolvedStudio(value);
  const scope = useScope(theme, 'sb-studio', finish);
  const box = useRef<HTMLDivElement>(null);
  // A largura da caixa. No computador, a prévia ocupa a caixa inteira: desenhada na largura dela
  // (nunca menos que a do aparelho) e reduzida só quando a caixa é menor. Tablet e celular ficam na
  // largura do aparelho, no meio.
  const [boxWidth, setBoxWidth] = useState(0);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setBoxWidth(el.clientWidth);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { width, zoom } = previewFit(device, boxWidth);
  const [height, setHeight] = useState(720);
  const onHeight = useCallback((h: number) => setHeight(h), []);
  // A letra maior muda a raiz do quadro, como no sistema; o tamanho do texto do acabamento também.
  const rootAttrs = useMemo(
    () => ({
      'data-text': reading === 'large' ? 'large' : 'normal',
      style: `--text-scale:${parseInt(finish.textSize, 10) / 14}`,
    }),
    [reading, finish.textSize],
  );
  const outer = height * zoom;
  const body = <ScreenView screen={screen} d={d} state={state} />;
  return (
    <FinishPreviewContext.Provider value={finish}>
      <div
        ref={box}
        aria-label={t('style.previewLabel')}
        role="region"
        className={`w-full min-w-0 overflow-hidden ${device === 'desk' ? '' : 'flex justify-center'}`}
        style={{ height: `${maxHeight ? Math.min(outer, maxHeight) : outer}px` }}
      >
        <IsolatedFrame width={width} zoom={zoom} rootAttrs={rootAttrs} onHeight={onHeight}>
          <div
            data-theme={dark ? 'dark' : 'light'}
            className={`${scope} flex min-h-screen bg-paper text-sm text-ink ${noColor ? 'grayscale' : ''}`}
          >
            <div
              className={`flex min-w-0 flex-1 ${reading === 'contrast' ? 'ui-contrast-more' : ''}`}
            >
              {device === 'phone' ? (
                <PhoneFrame d={d} nav={value.mobileNav ?? 'drawer'}>
                  {body}
                </PhoneFrame>
              ) : (
                <Frame id={value.layout.id} o={layout} d={d}>
                  {body}
                </Frame>
              )}
            </div>
          </div>
        </IsolatedFrame>
      </div>
    </FinishPreviewContext.Provider>
  );
}

/** As iniciais do nome, para o quadrinho da marca. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (
    words.length > 1 ? words[0]![0]! + words[1]![0]! : (words[0] ?? '?').slice(0, 2)
  ).toUpperCase();
}

function Frame({
  id,
  o,
  d,
  children,
}: {
  id: string;
  o: LayoutOptions;
  d: SampleDomain;
  children: ReactNode;
}) {
  const pad = { compact: 'p-5', comfortable: 'p-7', spacious: 'p-9' }[o.density];
  const body = (extra = '') => <main className={`min-w-0 flex-1 ${pad} ${extra}`}>{children}</main>;
  switch (id) {
    case 'dual':
      return (
        <>
          <Rail d={d} />
          <Column d={d} />
          <div className="flex min-w-0 flex-1 flex-col">
            <TopBar d={d} />
            {body()}
          </div>
        </>
      );
    case 'top':
      return (
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar d={d} areas dark />
          <SubStrip d={d} />
          {body(o.pageWidth === 'contained' ? 'mx-auto w-full max-w-5xl' : '')}
        </div>
      );
    case 'split':
      return (
        <>
          <Rail d={d} />
          <ListColumn d={d} width={o.listWidth} item={o.listItem} />
          <div className="flex min-w-0 flex-1 flex-col">
            <TopBar d={d} />
            {body()}
          </div>
        </>
      );
    case 'tabs':
      return (
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar d={d} areas dark />
          <WorkTabs d={d} />
          {body()}
          {o.statusBar === 'show' && <StatusBar d={d} />}
        </div>
      );
    case 'launcher':
      return (
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar d={d} dark />
          <Tiles d={d} icons={o.tiles === 'icons'} />
          {body('border-t border-rule')}
        </div>
      );
    case 'focus':
      return (
        <div className="flex min-w-0 flex-1 flex-col">
          <FocusBar d={d} topLinks={o.topLinks === 'show'} />
          {body(`mx-auto w-full ${o.contentWidth === 'narrow' ? 'max-w-3xl' : 'max-w-6xl'}`)}
        </div>
      );
    case 'sheet':
      return (
        <div className="flex min-w-0 flex-1 flex-col">
          <SheetTabs d={d} />
          <Ribbon d={d} compact={o.ribbon === 'compact'} />
          {body()}
        </div>
      );
    case 'inspector':
      return (
        <>
          <Sidebar d={d} o={o} />
          <div className="flex min-w-0 flex-1 flex-col">
            <TopBar d={d} />
            {body()}
          </div>
          <Inspector d={d} />
        </>
      );
    default:
      return (
        <>
          <Sidebar d={d} o={o} />
          <div className="flex min-w-0 flex-1 flex-col">
            <TopBar d={d} />
            {body(o.pageWidth === 'contained' ? 'mx-auto w-full max-w-5xl' : '')}
          </div>
        </>
      );
  }
}

/** O celular: barra de cima e o menu do celular escolhido (barra embaixo, gaveta ou grade). */
function PhoneFrame({ d, nav, children }: { d: SampleDomain; nav: string; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="ui-sidebar flex h-14 shrink-0 items-center gap-3 px-4">
        {nav !== 'bottom' &&
          (nav === 'grid' ? <LayoutGrid size={20} aria-hidden /> : <Menu size={20} aria-hidden />)}
        <span className="truncate text-sm font-semibold">{d.brand[0]}</span>
      </div>
      <main className="min-w-0 flex-1 p-4">{children}</main>
      {nav === 'bottom' && (
        <nav className="ui-sidebar sticky bottom-0 flex h-16 shrink-0 border-t border-rule">
          {d.areas.slice(0, 4).map((a) => {
            return (
              <span
                key={a.id}
                className={`flex flex-1 flex-col items-center justify-center gap-1 text-[11px] ${a.id === d.mainArea ? 'text-sidebar-link' : 'text-sidebar-muted'}`}
              >
                <SampleIcon name={a.icon} size={20} aria-hidden />
                {a.label}
              </span>
            );
          })}
          <span className="flex flex-1 flex-col items-center justify-center gap-1 text-[11px] text-sidebar-muted">
            <Menu size={20} aria-hidden />
            {t('nav.more')}
          </span>
        </nav>
      )}
    </div>
  );
}
